<script setup>

import { Form as VeeForm, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'
import { markRaw, ref, computed, onMounted } from 'vue'
import userService from '@/services/user.service'
import { useAccountStore } from '@/store/account.store'
import Icon from '@/components/Icon/Icon.vue'
import store from '@/store'
import router from '@/router'
import eventBus from '@/plugins/event-bus'
import { createToast } from 'mosha-vue-toastify'

const schema = markRaw(yup.object().shape({
  username: yup.string()
    .required("L'email est obligatoire")
    .email("Le format de l'email n'est pas correct")
    .nullable(),
  password: yup.string()
    .required("Le mot de passe est obligatoire")
    .nullable(),
  keep_session_open: yup.bool(),
}))
const message = ref("")
const form = ref({
  username: null,
  password: null,
  keep_session_open: false,
})
const loading = ref(false)
const eye = ref(false)

const loggedIn = computed(() => {
  return store.state.auth.status.loggedIn
})

const disabled = computed(() => {
  return loading.value
})

onMounted(() => {
  if (loggedIn.value) {
    router.push({ name: 'main' })
  }
})

const handleLogin = (credentials) => {
  loading.value = true

  store.dispatch('auth/login', credentials).then(
    async (response) => {
        store.commit('token/save', {
            'xsrf': response.xsrf_token,
            'refreshToken': response.refresh_token,
            'validitySessionMinutes': response.validity_session_minutes,
        })

        store.commit('profil/save', {
            'user': response.user,
            'authentication': response.authentication,
        })

        await userService.getAccounts().then((response) => {
          const accountStore = useAccountStore()
          accountStore.setAccounts(response.data.accounts)
        })

        eventBus.emit('refresh-validity-session', response.validity_session_minutes)

        createToast("Connexion réussie", {
            timeout: 3000,
            hideProgressBar: true,
            type: 'success',
            swipeClose: true,
            showIcon: false,
            position: 'top-center',
        })

        loading.value = false
        
        router.push({ name: 'main' });
    },
    (error) => {
      if(error.response?.data)
      {
        switch(error.response.data.error) {
          case 'no_credentials':
            message.value = "Email et/ou mot de passe erroné";
            break;
          default:
          message.value =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        }
      }

      loading.value = false
    }
  );
}

const viewPassword = () => {
  eye.value = !eye.value
}

</script>

<template>
  <div class="h-screen flex items-center justify-center">
    <div class="m-auto card card-container p-10 border-solid border border-rose-400 w-1/2 rounded max-w-md">
      <div class="flex justify-center text-xl mb-8">
          <strong class="font-bold">Identifiez-vous</strong>
      </div>

      <div class="form-group">
        <div v-if="message" class="bg-red-100 border border-red-400 text-red-700 px-4 py-1 mb-2 rounded relative" role="alert">
          <strong class="font-bold">{{ message }}</strong>
        </div>
      </div>

      <VeeForm @submit="handleLogin" :validation-schema="schema" class="flex flex-col">
        <div class="form-group">
          <label class="block text-sm font-medium text-slate-700" for="username">Email</label>
          <ErrorMessage name="username" v-slot="{ message }">
            <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-1 mt-2 rounded relative" role="alert">
              <strong class="font-bold">{{ message }}</strong>
            </div>
          </ErrorMessage>
          <Field v-model="form.username" name="username" type="text" class="form-control form-input" placeholder="ex.: email@domaine.com" />
        </div>
        <div class="form-group mt-4">
          <label class="block text-sm font-medium text-slate-700" for="password">Mot de passe</label>
          <ErrorMessage name="password" v-slot="{ message }">
            <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-1 mt-2 rounded relative" role="alert">
              <strong class="font-bold">{{ message }}</strong>
            </div>
          </ErrorMessage>
          <div v-show="!eye" class="flex flex-row cursor-pointer">
            <Field v-model="form.password" name="password" type="password" class="form-control form-input" />
            <div @click="viewPassword()">
              <Icon name="eye" color="grey" class="m-2 w-5 h-5" />
            </div>
          </div>
          <div v-show="eye" class="flex flex-row cursor-pointer">
            <Field v-model="form.password" name="password_view" type="text" class="form-control form-input" />
            <div @click="viewPassword()">
              <Icon name="eye-blocked" color="grey" class="m-2 w-5 h-5" />
            </div>
          </div>
        </div>

        <div class="form-group flex flex-col gap-2 justify-center mt-6">
          <button
            type="submit"
            :disabled="disabled"
            class="px-5 py-2 mt-4 shadow-xl rounded-full bg-black text-center text-white cursor-pointer"
          >
            <span v-if="!loading" class="font-empty">Connexion</span>
            <span v-if="loading" class="font-empty">Connexion en cours...</span>
          </button>
        </div>

        <div class="flex text-sm gap-2 mx-auto my-6">
          <Field v-model="form.keep_session_open" :value="true" name="keep_session_open" type="checkbox" class="form-control" />
          <span class="font-medium text-slate-700">Garder la session ouverte</span>
        </div>
      </VeeForm>

      <div class="mt-4">
        <a href="/reset-password" class="block text-sm font-medium text-slate-500 hover:underline">Mot de passe oublié ?</a>
      </div>
    </div>
  </div>
</template>
